import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Body, Button, H2 } from "@datapay-by-datacom/design-system";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import { FileDropzone, ImportProgress, WorkflowActionBar } from "../../shared";
import { SplitCostingImportConfirmModal } from "../components/SplitCostingImportConfirmModal";
import { SplitCostingImportEditModal } from "../components/SplitCostingImportEditModal";
import { SplitCostingImportReport } from "../components/SplitCostingImportReport";
import { SplitCostingWizardCtx } from "../components/SplitCostingWizardContext";
import {
  isSplitCostingValidationCommitReady,
  readApiWarnings,
} from "../hooks";
import {
  buildSplitCostingIssueReasonsByFileRow,
  computeSplitCostingWarnings,
  downloadSplitCostingImportIssuesCsv,
  mapSplitCostingApiWarnings,
  parseSplitCostingCsv,
  reconcileSplitCostingWarningAcknowledgements,
  splitCostingIssuesFilename,
  type SplitCostingImportWarning,
  type SplitCostingRow,
} from "../utils";
import styles from "./SplitCostingImportComposer.module.css";

type DialogMode = "replace" | "cancel" | "commit" | "override" | null;
const EMPTY_ISSUE_ROWS: SplitCostingRow[] = [];
const EMPTY_DISMISSED_WARNING_IDS: ReadonlySet<string> = new Set();

function useSplitCostingWizardContext() {
  const wizard = useContext(SplitCostingWizardCtx);
  if (!wizard) {
    throw new Error(
      "SplitCostingImportComposer must be rendered inside SplitCostingWizardProvider"
    );
  }
  return wizard;
}

export function SplitCostingImportComposer() {
  const wizard = useSplitCostingWizardContext();
  const {
    companyCode,
    companyName,
    mypayEnvironment,
    fileName,
    file,
    rawCsvText,
    parsedRows,
    parseErrors,
    failedUploadAttempt,
    isParentModalOpen,
    setUploadSuccess,
    setFailedUploadAttempt,
    clearUpload,
    manager,
  } = wizard;
  const datapayAwareValidations = useFeatureFlagValue(
    FeatureFlag.EnableSplitCostingDatapayAwareValidations
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(file);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [dismissedWarningIds, setDismissedWarningIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [commitBlockedMessage, setCommitBlockedMessage] = useState<
    string | null
  >(null);
  const shouldAutoValidateRef = useRef(false);
  const fileReadSequenceRef = useRef(0);
  const activeFileReaderRef = useRef<FileReader | null>(null);

  const isProcessing = manager.isValidating || manager.isCommitting;
  const visibleFailedAttempt = useMemo(
    () =>
      failedUploadAttempt ??
      (parseErrors.length > 0
        ? {
            fileName: fileName ?? "split-costing-import.csv",
            file,
            rawCsvText,
            parseErrors,
          }
        : null),
    [failedUploadAttempt, file, fileName, parseErrors, rawCsvText]
  );
  const hasEffectiveUncommittedProgress =
    manager.hasUncommittedImportProgress || visibleFailedAttempt !== null;

  useEffect(() => {
    setSelectedFile(file ?? visibleFailedAttempt?.file ?? null);
  }, [file, visibleFailedAttempt?.file]);

  useEffect(() => {
    if (!isParentModalOpen) return;
    setDialogMode(null);
    setEditingIndex(null);
    setReplacementFile(null);
  }, [isParentModalOpen]);

  useEffect(() => {
    if (
      !shouldAutoValidateRef.current ||
      !manager.hasRows ||
      manager.isConfigLoading
    ) {
      return;
    }
    shouldAutoValidateRef.current = false;
    void manager.validate();
  }, [manager.hasRows, manager.isConfigLoading, manager]);

  const warnings = useMemo(
    () =>
      datapayAwareValidations
        ? mapSplitCostingApiWarnings(
            readApiWarnings(manager.validationDisplayReport?.warnings),
            manager.rows
          )
        : computeSplitCostingWarnings(manager.rows),
    [datapayAwareValidations, manager.rows, manager.validationDisplayReport]
  );
  const warningSignature = warnings
    .map((warning) => `${warning.id}:${warning.message}`)
    .sort()
    .join("|");
  const previousWarningsRef = useRef<readonly SplitCostingImportWarning[]>(
    warnings
  );

  useEffect(() => {
    setDismissedWarningIds((dismissed) =>
      reconcileSplitCostingWarningAcknowledgements(
        dismissed,
        previousWarningsRef.current,
        warnings
      )
    );
    previousWarningsRef.current = warnings;
    // The signature captures meaningful warning changes even when IDs remain
    // stable because the same source rows now have a different total.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningSignature]);

  const activeWarnings = warnings.filter(
    (warning) => !dismissedWarningIds.has(warning.id)
  );
  const visibleParseErrors = visibleFailedAttempt?.parseErrors ?? [];
  const report = manager.commitResult ?? manager.validationDisplayReport;
  const reportPhase = manager.commitResult ? "commit" : "validation";
  const canConfirmCurrentValidation =
    manager.idempotencyKey !== null &&
    !manager.validationStale &&
    manager.submittedValidationRevision === manager.validationInputRevision &&
    isSplitCostingValidationCommitReady(
      manager.validationReport,
      manager.rows.length
    );
  const issuesUseFailedAttempt = visibleFailedAttempt !== null;
  const issueRows = issuesUseFailedAttempt ? EMPTY_ISSUE_ROWS : manager.rows;
  const issueRawCsvText = issuesUseFailedAttempt
    ? visibleFailedAttempt.rawCsvText
    : rawCsvText;
  const issueFileName = issuesUseFailedAttempt
    ? visibleFailedAttempt.fileName
    : fileName;
  const issueReasonsByFileRow = useMemo(
    () =>
      buildSplitCostingIssueReasonsByFileRow({
        rows: issueRows,
        result: issuesUseFailedAttempt ? null : report,
        warnings: issuesUseFailedAttempt ? [] : warnings,
        dismissedWarningIds: issuesUseFailedAttempt
          ? EMPTY_DISMISSED_WARNING_IDS
          : dismissedWarningIds,
        parseErrors: visibleFailedAttempt?.parseErrors ?? [],
      }),
    [
      issueRows,
      issuesUseFailedAttempt,
      report,
      warnings,
      dismissedWarningIds,
      visibleFailedAttempt,
    ]
  );
  const canDownloadIssues =
    issueRawCsvText !== null && issueReasonsByFileRow.size > 0;
  const handleDownloadIssues = useCallback(() => {
    if (!issueRawCsvText || issueReasonsByFileRow.size === 0) return;
    downloadSplitCostingImportIssuesCsv(
      issueRawCsvText,
      issueReasonsByFileRow,
      issueRows,
      splitCostingIssuesFilename(issueFileName)
    );
  }, [
    issueFileName,
    issueRawCsvText,
    issueReasonsByFileRow,
    issueRows,
  ]);

  const invalidateActiveFileRead = useCallback(() => {
    fileReadSequenceRef.current += 1;
    const activeReader = activeFileReaderRef.current;
    activeFileReaderRef.current = null;
    if (activeReader?.readyState === FileReader.LOADING) {
      activeReader.abort();
    }
  }, []);

  useEffect(
    () => () => {
      invalidateActiveFileRead();
    },
    [invalidateActiveFileRead]
  );

  const readFile = useCallback(
    (nextFile: File) => {
      const preserveCurrentBatch = manager.hasRows;
      const requestSequence = ++fileReadSequenceRef.current;
      const supersededReader = activeFileReaderRef.current;
      activeFileReaderRef.current = null;
      if (supersededReader?.readyState === FileReader.LOADING) {
        supersededReader.abort();
      }
      const reader = new FileReader();
      activeFileReaderRef.current = reader;
      const isCurrentRequest = () =>
        fileReadSequenceRef.current === requestSequence &&
        activeFileReaderRef.current === reader;
      const completeCurrentRequest = () => {
        if (activeFileReaderRef.current === reader) {
          activeFileReaderRef.current = null;
        }
      };
      reader.onload = (event) => {
        if (!isCurrentRequest()) return;
        completeCurrentRequest();
        const rawText = event.target?.result;
        if (typeof rawText !== "string") {
          const errors = [
            {
              fileRow: null,
              message: "Could not read the file. Please try again.",
            },
          ];
          setFailedUploadAttempt(nextFile.name, errors, nextFile, null);
          return;
        }

        const outcome = parseSplitCostingCsv(rawText, {
          retainOutOfRangePercentageRows: true,
        });
        if (!outcome.ok) {
          if (!preserveCurrentBatch) {
            manager.clear();
          }
          setFailedUploadAttempt(
            nextFile.name,
            outcome.errors,
            nextFile,
            rawText
          );
          return;
        }

        manager.clear();
        setDismissedWarningIds(new Set());
        setCommitBlockedMessage(null);
        setSelectedFile(nextFile);
        setUploadSuccess(nextFile.name, outcome.rows, nextFile, rawText);
        manager.setPendingRows(
          outcome.rows,
          companyCode,
          mypayEnvironment,
          nextFile.name
        );
        shouldAutoValidateRef.current = true;
      };
      reader.onerror = () => {
        if (!isCurrentRequest()) return;
        completeCurrentRequest();
        const errors = [
          {
            fileRow: null,
            message: "The file could not be read. Please try again.",
          },
        ];
        setFailedUploadAttempt(nextFile.name, errors, nextFile, null);
      };
      reader.onabort = () => {
        if (!isCurrentRequest()) return;
        completeCurrentRequest();
        const errors = [
          {
            fileRow: null,
            message: "File reading was interrupted. Please try again.",
          },
        ];
        setFailedUploadAttempt(nextFile.name, errors, nextFile, null);
      };
      reader.readAsText(nextFile);
    },
    [
      companyCode,
      manager,
      mypayEnvironment,
      setFailedUploadAttempt,
      setUploadSuccess,
    ]
  );

  const handleFileChange = useCallback(
    (next: File | File[] | null) => {
      if (!next || Array.isArray(next) || isProcessing) return;
      setCommitBlockedMessage(null);
      if (manager.hasUncommittedImportProgress) {
        setEditingIndex(null);
        setReplacementFile(next);
        setDialogMode("replace");
        return;
      }
      readFile(next);
    },
    [isProcessing, manager.hasUncommittedImportProgress, readFile]
  );

  const handleRequestEdit = useCallback(
    (rowIndex: number) => {
      if (isProcessing || isParentModalOpen) return;
      setDialogMode(null);
      setReplacementFile(null);
      setEditingIndex(rowIndex);
    },
    [isParentModalOpen, isProcessing]
  );

  const handleEditSave = useCallback(
    (rowIndex: number, updatedRow: SplitCostingRow) => {
      const nextRows = manager.rows.map((row, index) =>
        index === rowIndex ? updatedRow : row
      );
      const nextWarnings = computeSplitCostingWarnings(nextRows, {
        skipOverAllocation: datapayAwareValidations,
      });
      setDismissedWarningIds((dismissed) =>
        reconcileSplitCostingWarningAcknowledgements(
          dismissed,
          warnings,
          nextWarnings
        )
      );
      previousWarningsRef.current = nextWarnings;
      manager.updateRow(rowIndex, updatedRow);
      setCommitBlockedMessage(null);
    },
    [datapayAwareValidations, manager, warnings]
  );

  const handleDismissWarning = useCallback((warningId: string) => {
    setDismissedWarningIds((current) => new Set(current).add(warningId));
    setCommitBlockedMessage(null);
  }, []);

  const requestDialog = (mode: Exclude<DialogMode, null>) => {
    if (isProcessing || isParentModalOpen) return;
    setEditingIndex(null);
    if (mode !== "replace") setReplacementFile(null);
    setDialogMode(mode);
  };

  const handleRequestCommit = () => {
    if (!canConfirmCurrentValidation) {
      setCommitBlockedMessage(
        "Nothing has been imported. Resolve every blocking error and re-validate before confirming the import."
      );
      return;
    }
    setCommitBlockedMessage(null);
    requestDialog(activeWarnings.length > 0 ? "override" : "commit");
  };

  const handleCommit = () => {
    setDialogMode(null);
    void manager.commit(true);
  };

  const clearImport = () => {
    invalidateActiveFileRead();
    setDialogMode(null);
    setEditingIndex(null);
    setReplacementFile(null);
    setSelectedFile(null);
    setDismissedWarningIds(new Set());
    setCommitBlockedMessage(null);
    manager.clear();
    clearUpload();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Upload split costing CSV</H2>
        <Body>
          Importing for <strong>{companyName || companyCode}</strong>.
        </Body>
      </div>

      <FileDropzone
        value={selectedFile}
        compactWhenSelected
        onChange={handleFileChange}
        instruction="Drop your CSV file here, or click to browse"
        subInstruction="CSV must contain Employee Code, Apportioned Percentage, and the costing path. Employee Name must not be included."
        accept=".csv"
        multiple={false}
        isDisabled={isProcessing}
        buttonText="Select CSV file"
        fileFilter={(candidate) =>
          candidate.name.toLowerCase().endsWith(".csv")
        }
      />

      {visibleParseErrors.length > 0 && (
        <section className={styles.parseError} role="alert">
          <h3>Could not parse this file</h3>
          {visibleFailedAttempt && (
            <p>
              <strong>{visibleFailedAttempt.fileName}</strong> was not accepted.
              The previously parsed batch, if any, is unchanged.
            </p>
          )}
          <ul>
            {visibleParseErrors.map((error, index) => (
              <li key={`${error.fileRow ?? "file"}:${index}`}>
                {error.fileRow !== null && `Row ${error.fileRow}: `}
                {error.message}
              </li>
            ))}
          </ul>
          <div className={styles.parseFeedbackActions}>
            <p>
              Fix the source CSV and upload it again. File-level issues cannot
              be edited in the portal.
            </p>
            {canDownloadIssues && (
              <Button
                variant="secondary"
                onPress={handleDownloadIssues}
                isDisabled={isProcessing || isParentModalOpen}
              >
                Download issues CSV
              </Button>
            )}
          </div>
        </section>
      )}

      {manager.isValidating && (
        <ImportProgress
          heading="Validating import…"
          progressbarLabel="Split costing validation in progress"
          detail={`Checking ${manager.rows.length} row${
            manager.rows.length === 1 ? "" : "s"
          }…`}
        />
      )}
      {manager.isCommitting && (
        <ImportProgress
          heading="Importing split costing…"
          progressbarLabel="Split costing import in progress"
          detail={`Importing ${manager.rows.length} row${
            manager.rows.length === 1 ? "" : "s"
          } as one batch…`}
        />
      )}
      {manager.managerError && (
        <div className={styles.parseError} role="alert">
          {manager.managerError}
        </div>
      )}

      {hasEffectiveUncommittedProgress && !report && !isProcessing && (
        <WorkflowActionBar
          ariaLabel="Split costing import actions"
          leadingActions={
            <Button
              variant="secondary"
              onPress={() => requestDialog("cancel")}
            >
              Cancel import
            </Button>
          }
          trailingActions={
            manager.hasRows ? (
              <Button variant="primary" onPress={() => void manager.validate()}>
                Validate
              </Button>
            ) : undefined
          }
        />
      )}

      {report && (
        <SplitCostingImportReport
          rows={manager.rows}
          result={report}
          resultPhase={reportPhase}
          warnings={warnings}
          dismissedWarningIds={dismissedWarningIds}
          onDismissWarning={handleDismissWarning}
          onEditRow={handleRequestEdit}
          validationStale={manager.validationStale}
          canConfirmImport={canConfirmCurrentValidation}
          commitBlockedMessage={commitBlockedMessage}
          onCancelImport={() => requestDialog("cancel")}
          onValidate={() => void manager.validate()}
          onRequestCommit={handleRequestCommit}
          onStartNewImport={clearImport}
          isValidating={manager.isValidating}
          isCommitting={manager.isCommitting}
          isProcessing={isParentModalOpen}
          canDownloadIssues={canDownloadIssues}
          onDownloadIssues={handleDownloadIssues}
        />
      )}

      <SplitCostingImportEditModal
        isOpen={!isParentModalOpen && editingIndex !== null}
        row={
          editingIndex === null ? null : manager.rows[editingIndex] ?? null
        }
        rowIndex={editingIndex}
        onClose={() => setEditingIndex(null)}
        onSave={handleEditSave}
        isProcessing={isProcessing}
      />

      {dialogMode && (
        <SplitCostingImportConfirmModal
          isOpen={!isParentModalOpen}
          mode={dialogMode}
          rowCount={manager.rows.length || parsedRows?.length || 0}
          warningCount={activeWarnings.length}
          replacementFileName={replacementFile?.name}
          companyName={companyName}
          companyCode={companyCode}
          environment={mypayEnvironment}
          onSafeAction={() => {
            setDialogMode(null);
            if (dialogMode === "replace") setReplacementFile(null);
          }}
          onConfirmAction={() => {
            if (dialogMode === "replace") {
              const nextFile = replacementFile;
              setDialogMode(null);
              setReplacementFile(null);
              if (nextFile) readFile(nextFile);
              return;
            }
            if (dialogMode === "cancel") {
              clearImport();
              return;
            }
            handleCommit();
          }}
        />
      )}
    </div>
  );
}
